// Coordinator Search · v2.8+E325
// Audit + search tool for dispatch/driver/rider trip records. Filters map 1:1
// to GET /api/admin/coordinator-search; clicking a result opens the full
// merged detail (GET .../record/{record_id}). Read-only.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RIDER_FLAGS, isCriticalFlag } from "@/lib/riderFlags";
import {
  Search, X, Loader2, MapPin, Flag, User, Phone, Car, Headset, Users,
  Clock, CheckCircle2, CalendarClock, RotateCw, ChevronRight, Hash, Building2,
  ArrowUp, ArrowDown, Pencil, DollarSign, StickyNote, ListFilter, Globe,
} from "lucide-react";

const SOURCE_META = {
  rider_callin:      { label: "Rider Called In",       cls: "bg-sky-500/15 text-sky-300 border-sky-500/40",           Icon: Phone },
  dispatch_initiated:{ label: "Dispatch Initiated",    cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",     Icon: Headset },
  driver_initiated:  { label: "Driver Initiated",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: Car },
  rider_online:      { label: "Rider Online Booking",  cls: "bg-violet-500/15 text-violet-300 border-violet-500/40",   Icon: Globe },
};

const STATUS_META = {
  completed:   { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", rail: "bg-emerald-500" },
  cancelled:   { cls: "bg-red-500/15 text-red-300 border-red-500/40",             rail: "bg-red-500" },
  in_progress: { cls: "bg-sky-500/15 text-sky-300 border-sky-500/40",             rail: "bg-sky-500" },
  assigned:    { cls: "bg-violet-500/15 text-violet-300 border-violet-500/40",    rail: "bg-violet-500" },
  queued:      { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",       rail: "bg-amber-500" },
  unknown:     { cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600",             rail: "bg-zinc-600" },
};

const fmtTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const EMPTY = {
  q: "", name: "", phone: "", trip_id: "", address: "", city: "",
  driver: "", dispatcher: "", date_from: "", date_to: "",
  source: "", status: "", flag: "", group_ride: false, edits_only: false,
  sort_by: "created", sort_dir: "desc",
};

// v2.8+E329 · Infinite scroll — render results in small pages so the DOM
// stays light no matter how large the matched set is.
const PAGE_SIZE = 30;

const buildParams = (filters, extra) => {
  const params = { ...extra };
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== false) params[k] = v;
  });
  return params;
};

const SORT_OPTS = [
  { value: "created", label: "Date / Time" },
  { value: "completed", label: "Completed Time" },
  { value: "assigned", label: "Assigned Time" },
  { value: "driver", label: "Driver" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "customer", label: "Customer Name" },
  { value: "status", label: "Trip Status" },
];

const PRESETS = [
  { key: "all",      label: "All Records",    Icon: ListFilter, sort_by: "created",    sort_dir: "desc", hint: "Search and audit every dispatch, driver, and rider trip." },
  { key: "driver",   label: "Driver Audit",   Icon: Car,        sort_by: "driver",     sort_dir: "asc",  hint: "Enter a Driver Name (and optional date range) to audit every trip assigned to that driver." },
  { key: "rider",    label: "Rider Audit",    Icon: User,       sort_by: "customer",   sort_dir: "asc",  hint: "Enter a Customer Name or Phone to audit all of a rider's trips — any flags on their record are shown." },
  { key: "dispatch", label: "Dispatch Audit", Icon: Headset,    sort_by: "dispatcher", sort_dir: "asc",  hint: "Enter a Dispatcher Name to audit every trip they initiated or edited — price & note edits are surfaced." },
];

function FlagChips({ flags }) {
  if (!flags?.length) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="cs-flag-chips">
      {flags.map((f) => (
        <span
          key={f}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border ${
            isCriticalFlag(f)
              ? "bg-red-600/30 text-white border-red-400 animate-pulse"
              : "bg-red-500/15 text-red-300 border-red-500/40"
          }`}
        >
          <Flag className="h-2.5 w-2.5" />{f}
        </span>
      ))}
    </div>
  );
}

function LabeledField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "bg-zinc-950 border-zinc-700 text-zinc-100 h-9 text-sm placeholder:text-zinc-600";

function ResultRow({ rec, onOpen }) {
  const st = STATUS_META[rec.status_key] || STATUS_META.unknown;
  const src = SOURCE_META[rec.source] || SOURCE_META.rider_online;
  const SrcIcon = src.Icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(rec)}
      data-testid={`cs-result-${rec.record_id}`}
      className="group w-full text-left relative overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/60 hover:border-amber-500/50 hover:bg-zinc-900 transition-colors"
    >
      <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${st.rail}`} />
      <div className="pl-4 pr-3 py-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 font-mono text-xs text-amber-300 font-bold">
            <Hash className="h-3 w-3" />{rec.trip_id}
          </span>
          <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold border ${st.cls}`} data-testid={`cs-status-${rec.record_id}`}>{rec.status}</span>
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border ${src.cls}`} data-testid={`cs-source-${rec.record_id}`}>
            <SrcIcon className="h-2.5 w-2.5" />{src.label}
          </span>
          {rec.group_ride && (
            <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40">
              <Users className="h-2.5 w-2.5" />Group
            </span>
          )}
          {rec.edits_count > 0 && (
            <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border bg-orange-500/15 text-orange-300 border-orange-500/40" data-testid={`cs-edits-${rec.record_id}`} title={`${rec.edits_count} edit(s) by ${(rec.edit_actors || []).join(", ")}`}>
              <Pencil className="h-2.5 w-2.5" />{rec.edits_count} edit{rec.edits_count === 1 ? "" : "s"}
            </span>
          )}
          {rec.has_price_edit && (
            <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border bg-amber-500/15 text-amber-300 border-amber-500/40" title="Price was edited"><DollarSign className="h-2.5 w-2.5" />Price</span>
          )}
          {rec.has_notes_edit && (
            <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border bg-sky-500/15 text-sky-300 border-sky-500/40" title="Notes were edited"><StickyNote className="h-2.5 w-2.5" />Notes</span>
          )}
          <FlagChips flags={rec.rider_flags} />
          <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:text-amber-400 ml-auto flex-shrink-0" />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="inline-flex items-center gap-1.5 text-zinc-100 font-semibold">
            <User className="h-3.5 w-3.5 text-zinc-500" />{rec.customer_name || "—"}
          </span>
          <span className="inline-flex items-center gap-1.5 text-zinc-400">
            <Phone className="h-3.5 w-3.5 text-zinc-600" />{rec.customer_phone || "—"}
          </span>
        </div>

        <div className="grid gap-1 text-xs text-zinc-300">
          <span className="inline-flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" /><span className="truncate">{rec.pickup || "—"}</span></span>
          <span className="inline-flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" /><span className="truncate">{rec.dropoff || "—"}</span></span>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" />Driver: <span className="text-zinc-300">{rec.driver_name || "Unassigned"}</span></span>
          <span className="inline-flex items-center gap-1"><Headset className="h-3 w-3" />Dispatcher: <span className="text-zinc-300">{rec.dispatcher_name || "—"}</span></span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 pt-1 border-t border-zinc-800/70">
          <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><CalendarClock className="h-3 w-3 text-zinc-500" />Created: <span className="text-zinc-300">{fmtTs(rec.created_at)}</span></span>
          <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><Clock className="h-3 w-3 text-violet-400" />Assigned: <span className="text-zinc-300">{fmtTs(rec.assigned_at)}</span></span>
          <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><CheckCircle2 className="h-3 w-3 text-emerald-400" />Completed: <span className="text-zinc-300">{fmtTs(rec.completed_at)}</span></span>
        </div>
      </div>
    </button>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5 border-b border-zinc-800/60">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</span>
      <span className={`text-sm text-zinc-100 break-words ${mono ? "font-mono text-amber-300" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

function DetailModal({ record, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!record) return;
    let cancelled = false;
    setLoading(true); setData(null);
    adminApi.get(`/admin/coordinator-search/record/${record.record_id}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [record]);

  const s = data?.summary || record || {};
  const src = SOURCE_META[s.source] || SOURCE_META.rider_online;

  return (
    <Dialog open={!!record} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="cs-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Hash className="h-4 w-4 text-amber-400" />
            <span className="font-mono text-amber-300">{s.trip_id}</span>
            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border ${src.cls}`}>{src.label}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-500" data-testid="cs-detail-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <FlagChips flags={s.rider_flags} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <DetailRow label="Customer Name" value={s.customer_name} />
              <DetailRow label="Phone Number" value={s.customer_phone} />
              <DetailRow label="Trip ID" value={s.trip_id} mono />
              <DetailRow label="Trip Status" value={s.status} />
              <DetailRow label="Pickup Address" value={s.pickup} />
              <DetailRow label="Drop-off Address" value={s.dropoff} />
              <DetailRow label="City" value={s.city} />
              <DetailRow label="Ride Type" value={s.ride_type} />
              <DetailRow label="Driver Assigned" value={s.driver_name || "Unassigned"} />
              <DetailRow label="Dispatcher Assigned" value={s.dispatcher_name} />
              <DetailRow label="Trip Source" value={src.label} />
              <DetailRow label="Group Ride" value={s.group_ride ? "Yes" : "No"} />
              <DetailRow label="Fare Total" value={s.total != null ? `$${Number(s.total).toFixed(2)}` : "—"} />
              <DetailRow label="Source (raw)" value={s.source_raw} />
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Timeline</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div><div className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><CalendarClock className="h-3 w-3" />Created</div><div className="text-zinc-100">{fmtTs(s.created_at)}</div></div>
                <div><div className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><Clock className="h-3 w-3 text-violet-400" />Assigned</div><div className="text-zinc-100">{fmtTs(s.assigned_at)}</div></div>
                <div><div className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><CheckCircle2 className="h-3 w-3 text-emerald-400" />Completed</div><div className="text-zinc-100">{fmtTs(s.completed_at)}</div></div>
              </div>
            </div>

            {s.rider_flags_audit?.length > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Flag Audit Trail</div>
                {s.rider_flags_audit.map((a, i) => (
                  <div key={i} className="text-[11px] text-zinc-400 flex items-start gap-1.5">
                    <Flag className="h-2.5 w-2.5 text-red-400 mt-0.5" />
                    <span><span className="font-bold text-zinc-200">{a.flag}</span> · added by {a.by || "Dispatch"}{a.at ? ` · ${fmtTs(a.at)}` : ""}</span>
                  </div>
                ))}
              </div>
            )}

            {s.edits?.length > 0 && (
              <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3 space-y-2" data-testid="cs-detail-edits">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold flex items-center gap-1"><Pencil className="h-3 w-3" />Edit History · Price &amp; Notes</div>
                {s.edits.map((e, i) => (
                  <div key={i} className="text-[11px] text-zinc-300 border-b border-zinc-800/60 pb-1.5 last:border-0 last:pb-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-bold text-zinc-100">{e.actor || "Unknown"}</span>
                      <span className="rounded bg-zinc-800 px-1 py-0.5 text-[9px] uppercase tracking-wide text-zinc-400">{(e.action || "edit").replace(/_/g, " ")}</span>
                      {e.supervisor_override && <span className="rounded bg-red-500/20 text-red-300 px-1 py-0.5 text-[9px] font-bold">override</span>}
                      <span className="text-zinc-500 ml-auto">{fmtTs(e.ts)}</span>
                    </div>
                    {e.changes && Object.keys(e.changes).length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(e.changes).map(([field, val]) => (
                          <div key={field} className="text-[10px] text-zinc-400">
                            <span className="font-semibold text-zinc-300">{field}</span>:{" "}
                            {val && typeof val === "object" && ("from" in val || "to" in val)
                              ? <><span className="text-red-300 line-through">{String(val.from ?? "—")}</span> <span className="text-zinc-500">→</span> <span className="text-emerald-300">{String(val.to ?? "—")}</span></>
                              : <span className="text-zinc-200">{String(val)}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {e.note && <div className="mt-0.5 text-[10px] text-zinc-500 italic">“{e.note}”</div>}
                  </div>
                ))}
              </div>
            )}

            {data?.raw && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowRaw((v) => !v)}
                  data-testid="cs-detail-raw-toggle"
                  className="text-[11px] text-zinc-500 hover:text-amber-400 font-semibold underline underline-offset-2"
                >
                  {showRaw ? "Hide" : "Show"} full raw booking data
                </button>
                {showRaw && (
                  <pre className="mt-2 max-h-72 overflow-auto rounded-lg border border-zinc-800 bg-black/60 p-3 text-[10px] leading-relaxed text-zinc-400" data-testid="cs-detail-raw">
                    {JSON.stringify(data.raw, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CoordinatorSearchTab() {
  const [f, setF] = useState(EMPTY);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [returned, setReturned] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [preset, setPreset] = useState("all");
  const inFlight = useRef(false);
  const resultsRef = useRef([]);
  const sentinelRef = useRef(null);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // v2.8+E326 · `silent` polls refresh results in place (no spinner / no reset)
  // so live DB changes surface without a reload.
  // v2.8+E329 · A fresh search loads only the first page (PAGE_SIZE); a silent
  // poll refreshes the whole currently-loaded window so infinite-scroll pages
  // aren't lost and the DOM node count stays flat.
  const runSearch = useCallback(async (filters, { silent = false } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setAppliedFilters(filters);
    if (!silent) setLoading(true);
    const windowLimit = silent ? Math.max(PAGE_SIZE, resultsRef.current.length) : PAGE_SIZE;
    try {
      const params = buildParams(filters, { limit: windowLimit, offset: 0 });
      const { data } = await adminApi.get("/admin/coordinator-search", { params });
      const rows = data.results || [];
      setResults(rows);
      resultsRef.current = rows;
      setTotal(data.total || 0);
      setReturned(rows.length);
      setHasMore(!!data.has_more);
      setLastUpdated(new Date());
    } catch {
      if (!silent) {
        setResults([]); resultsRef.current = [];
        setTotal(0); setReturned(0); setHasMore(false);
      }
    } finally {
      inFlight.current = false;
      if (!silent) setLoading(false);
    }
  }, []);

  // v2.8+E329 · Append the next page (triggered by the scroll sentinel).
  const loadMore = useCallback(async () => {
    if (inFlight.current || loadingMore) return;
    inFlight.current = true;
    setLoadingMore(true);
    try {
      const offset = resultsRef.current.length;
      const params = buildParams(appliedFilters, { limit: PAGE_SIZE, offset });
      const { data } = await adminApi.get("/admin/coordinator-search", { params });
      const seen = new Set();
      const merged = [...resultsRef.current, ...(data.results || [])].filter(
        (r) => (seen.has(r.record_id) ? false : seen.add(r.record_id)),
      );
      setResults(merged);
      resultsRef.current = merged;
      setTotal(data.total || 0);
      setReturned(merged.length);
      setHasMore(!!data.has_more);
    } catch {
      /* keep what we already have */
    } finally {
      inFlight.current = false;
      setLoadingMore(false);
    }
  }, [appliedFilters, loadingMore]);

  useEffect(() => { runSearch(EMPTY); }, [runSearch]);

  // v2.8+E326 · Live data polling — re-run the ACTIVE filters every 15s so the
  // list reflects current DB state (new trips become discoverable immediately)
  // without a page reload. Pauses while the tab is hidden; refreshes on return.
  useEffect(() => {
    const tick = () => { if (!document.hidden) runSearch(appliedFilters, { silent: true }); };
    const id = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) runSearch(appliedFilters, { silent: true }); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [appliedFilters, runSearch]);

  // v2.8+E329 · Infinite scroll — observe a sentinel below the list and pull the
  // next page as it nears the viewport.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) loadMore();
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  const onSubmit = (e) => { e?.preventDefault?.(); runSearch(f); };
  const onClear = () => { setPreset("all"); setF(EMPTY); runSearch(EMPTY); };

  // v2.8+E327 · Audit presets reorder the results for a driver/rider/dispatch
  // audit; sort controls are applied server-side and re-run immediately.
  const applyPreset = (p) => {
    setPreset(p.key);
    setF((prev) => { const next = { ...prev, sort_by: p.sort_by, sort_dir: p.sort_dir }; runSearch(next); return next; });
  };
  const setSort = (key) => setF((prev) => { const next = { ...prev, sort_by: key }; runSearch(next); return next; });
  const toggleDir = () => setF((prev) => { const next = { ...prev, sort_dir: prev.sort_dir === "asc" ? "desc" : "asc" }; runSearch(next); return next; });

  const activeCount = useMemo(
    () => Object.entries(f).filter(([k, v]) => !["sort_by", "sort_dir"].includes(k) && v !== "" && v !== false).length,
    [f],
  );

  const presetHint = (PRESETS.find((p) => p.key === preset) || PRESETS[0]).hint;

  return (
    <div className="space-y-4" data-testid="coordinator-search-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 data-testid="coordinator-search-title" className="text-2xl font-black tracking-tight text-zinc-100 flex items-center gap-2">
            <Search className="h-6 w-6 text-amber-400" />Coordinator Search
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">Audit and search dispatch, driver, and rider trip records.</p>
        </div>
      </div>

      <div className="flex flex-col gap-2" data-testid="cs-audit-presets">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => {
            const PIcon = p.Icon;
            const on = preset === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p)}
                data-testid={`cs-preset-${p.key}`}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "bg-amber-400 text-zinc-950 border-amber-400" : "border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-300"}`}
              >
                <PIcon className="h-3.5 w-3.5" />{p.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-zinc-500" data-testid="cs-preset-hint">{presetHint}</p>
      </div>

      <form onSubmit={onSubmit} className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 space-y-4" data-testid="cs-filter-panel">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="cs-filter-q"
            value={f.q}
            onChange={(e) => set("q", e.target.value)}
            placeholder="Search anything — name, phone, trip ID, address, driver, dispatcher…"
            className="bg-zinc-950 border-zinc-700 text-zinc-100 h-11 pl-10 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <LabeledField label="Customer Name"><Input data-testid="cs-filter-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Sam" /></LabeledField>
          <LabeledField label="Phone Number"><Input data-testid="cs-filter-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="e.g. 5805551234" /></LabeledField>
          <LabeledField label="Trip ID"><Input data-testid="cs-filter-trip-id" value={f.trip_id} onChange={(e) => set("trip_id", e.target.value)} className={inputCls} placeholder="e.g. BTS-DISP" /></LabeledField>
          <LabeledField label="Pickup / Drop-off"><Input data-testid="cs-filter-address" value={f.address} onChange={(e) => set("address", e.target.value)} className={inputCls} placeholder="Address contains…" /></LabeledField>
          <LabeledField label="City"><Input data-testid="cs-filter-city" value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} placeholder="e.g. Weatherford" /></LabeledField>
          <LabeledField label="Driver Name"><Input data-testid="cs-filter-driver" value={f.driver} onChange={(e) => set("driver", e.target.value)} className={inputCls} placeholder="Driver contains…" /></LabeledField>
          <LabeledField label="Dispatcher Name"><Input data-testid="cs-filter-dispatcher" value={f.dispatcher} onChange={(e) => set("dispatcher", e.target.value)} className={inputCls} placeholder="Dispatcher contains…" /></LabeledField>

          <LabeledField label="Trip Source">
            <Select value={f.source || "all"} onValueChange={(v) => set("source", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cs-filter-source" className={inputCls}><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="rider_callin">Rider Called In</SelectItem>
                <SelectItem value="dispatch_initiated">Dispatch Initiated</SelectItem>
                <SelectItem value="driver_initiated">Driver Initiated</SelectItem>
                <SelectItem value="rider_online">Rider Online Booking</SelectItem>
              </SelectContent>
            </Select>
          </LabeledField>

          <LabeledField label="Trip Status">
            <Select value={f.status || "all"} onValueChange={(v) => set("status", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cs-filter-status" className={inputCls}><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </LabeledField>

          <LabeledField label="Rider Flag">
            <Select value={f.flag || "all"} onValueChange={(v) => set("flag", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cs-filter-flag" className={inputCls}><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-72">
                <SelectItem value="all">All (any / none)</SelectItem>
                <SelectItem value="__any__">Any flagged rider</SelectItem>
                {RIDER_FLAGS.map((fl) => (
                  <SelectItem key={fl} value={fl.toLowerCase()}>{fl}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </LabeledField>

          <LabeledField label="Date From"><Input data-testid="cs-filter-date-from" type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className={inputCls} /></LabeledField>
          <LabeledField label="Date To"><Input data-testid="cs-filter-date-to" type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className={inputCls} /></LabeledField>

          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Switch data-testid="cs-filter-group" checked={f.group_ride} onCheckedChange={(v) => set("group_ride", v)} />
              <span className="inline-flex items-center gap-1 text-sm text-zinc-300"><Users className="h-3.5 w-3.5 text-fuchsia-400" />Group rides only</span>
            </label>
          </div>

          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Switch data-testid="cs-filter-edits" checked={f.edits_only} onCheckedChange={(v) => set("edits_only", v)} />
              <span className="inline-flex items-center gap-1 text-sm text-zinc-300"><Pencil className="h-3.5 w-3.5 text-orange-400" />Edited trips only</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Button type="submit" data-testid="cs-search-btn" disabled={loading} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Search className="h-4 w-4 mr-1.5" />}Search
          </Button>
          <Button type="button" variant="outline" data-testid="cs-clear-btn" onClick={onClear} className="border-zinc-700 text-zinc-300 hover:text-zinc-100">
            <X className="h-4 w-4 mr-1.5" />Clear{activeCount > 0 ? ` (${activeCount})` : ""}
          </Button>
          <Button type="button" variant="ghost" data-testid="cs-refresh-btn" onClick={() => runSearch(f)} className="text-zinc-500 hover:text-amber-400">
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-between text-sm text-zinc-400 flex-wrap gap-2" data-testid="cs-results-header">
        <span><span className="font-bold text-zinc-100" data-testid="cs-results-count">{total}</span> result{total === 1 ? "" : "s"}{returned < total ? ` · showing ${returned}` : ""}</span>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <span className="text-[11px] text-zinc-500">Sort</span>
          <Select value={f.sort_by} onValueChange={setSort}>
            <SelectTrigger data-testid="cs-sort-by" className="h-8 w-[150px] bg-zinc-950 border-zinc-700 text-zinc-200 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
              {SORT_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" size="sm" variant="outline" onClick={toggleDir} data-testid="cs-sort-dir" className="h-8 px-2 border-zinc-700 text-zinc-300 hover:text-zinc-100" title={f.sort_dir === "asc" ? "Ascending" : "Descending"}>
            {f.sort_dir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          </Button>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 font-semibold" data-testid="cs-live-indicator" title="Results auto-refresh every 15s">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Live{lastUpdated ? ` · updated ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-zinc-500" data-testid="cs-loading">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
      ) : results.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="cs-empty">
          <Building2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold">No matching records</p>
          <p className="text-xs mt-1">Adjust your filters and search again.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-2.5" data-testid="cs-results-list">
            {results.map((rec) => (
              <ResultRow key={rec.record_id} rec={rec} onOpen={setSelected} />
            ))}
          </div>
          <div ref={sentinelRef} data-testid="cs-scroll-sentinel" aria-hidden className="h-4" />
          {loadingMore && (
            <div className="py-4 flex items-center justify-center text-zinc-500" data-testid="cs-loading-more">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading more…
            </div>
          )}
          {!hasMore && !loadingMore && results.length > 0 && (
            <p className="py-3 text-center text-[11px] text-zinc-600" data-testid="cs-end-of-list">
              End of results · {results.length} shown
            </p>
          )}
        </>
      )}

      {selected && <DetailModal record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
